import Button from '@carvana/ds-button';
import Link from '@carvana/ds-link';
import cx from 'classnames';
import { useContext, useEffect, useRef, useState } from 'react';
import InternalLinkContext from '~/context/home/InternalLinkContext';
import { nativeSrpPayload } from '~/native/common/utils';
import {
  CategoryHeader,
  BrowseByLabel,
  LinkGrid,
  SEOSectionWrapper,
  TabsContainer,
  TabButton,
  TabButtonsWrapper,
  TabContentContainer,
  TabWrapper
} from './InternalLinks.styles';
import { InternalLinkType } from './InternalLinks.types';

const TAB_CONFIG = [
  {
    id: InternalLinkType.PopularMakesModels,
    label: 'Popular makes and models'
  },
  { id: InternalLinkType.BodyStyle, label: 'Styles' },
  { id: InternalLinkType.TopLocations, label: 'Locations' }
];

export default function InternalLinks() {
  const { internalLinksGroups } = useContext(InternalLinkContext);
  const [activeTab, setActiveTab] = useState<InternalLinkType>(
    InternalLinkType.PopularMakesModels
  );
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isToggleHidden = activeTab === InternalLinkType.BodyStyle;

  useEffect(() => {
    if (shouldScroll) {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  return (
    <SEOSectionWrapper
      ref={sectionRef}
      analyticsSection="Internal Links"
      data-testid="internal-links-section"
      hasSideSpacing
    >
      <CategoryHeader>
        <BrowseByLabel>Browse by </BrowseByLabel>
        <TabsContainer>
          <TabButtonsWrapper>
            {TAB_CONFIG.map(tab => (
              <TabButton
                key={tab.id}
                isActive={activeTab === tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (isExpanded) {
                    setIsExpanded(false);
                    setShouldScroll(true);
                  }
                }}
                data-testid={`internal-links-tab-${tab.id}`}
                data-analytics-id="Internal Links Tab"
                data-analytics-attributes={JSON.stringify({
                  tab: tab.label
                })}
              >
                {tab.label}
              </TabButton>
            ))}
          </TabButtonsWrapper>
        </TabsContainer>
      </CategoryHeader>
      <TabContentContainer isExpanded={isExpanded}>
        {Object.entries(internalLinksGroups).map(([tabId, content]) => {
          const typedTabId = tabId as InternalLinkType;

          return (
            <TabWrapper
              key={tabId}
              tabId={typedTabId}
              isActive={typedTabId === activeTab}
              aria-hidden={typedTabId !== activeTab}
            >
              <LinkGrid tabId={typedTabId}>
                {content.links.map(({ link, text }) => (
                  <Link
                    key={text}
                    href={`/cars${link}?refSource=home`}
                    data-testid="internal-link"
                    data-analytics-id="Internal Link"
                    data-native-payload={nativeSrpPayload}
                    data-analytics-attributes={JSON.stringify({
                      link: text,
                      category: typedTabId
                    })}
                    size="sm"
                    className="text-text-primary no-underline"
                  >
                    {text}
                  </Link>
                ))}
              </LinkGrid>
            </TabWrapper>
          );
        })}
      </TabContentContainer>
      <Button
        variant="tertiary"
        size="sm"
        className={cx('w-full sm:w-[104px] md:hidden', {
          'pointer-events-none invisible': isToggleHidden
        })}
        onClick={() => {
          setIsExpanded(!isExpanded);
          setShouldScroll(true);
        }}
        aria-hidden={isToggleHidden}
        tabIndex={isToggleHidden ? -1 : undefined}
        data-testid="internal-links-view-more"
        data-analytics-id="Internal Links View More"
        data-analytics-attributes={JSON.stringify({
          expanded: !isExpanded
        })}
      >
        {isExpanded ? 'View Less' : 'View More'}
      </Button>
    </SEOSectionWrapper>
  );
}
